import { useEffect, useState } from 'react';

import { useStoreContext } from 'features/store';
import { isEmpty } from 'lodash-es';

import { ItemAvailabilityStatus } from 'enums/menu';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useLoyaltyIncentivesAvailabilityUtils } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { IMenuObjectWithDaypart, getAvailabilityStatus, itemIsAvailable } from 'utils/availability';
import { getOneMenuAvailability } from 'utils/availability/one-menu-availability';
import { IWithVendorConfig } from 'utils/vendor-config';

import { useLoyaltyContext } from '../loyalty-context';

import { IncentiveEvaluationMap } from './types';
import {
  buildInCooldownEvaluationResult,
  buildOutOfDayPartEvaluationResult,
  buildVendorEvaluationResult,
} from './utils/incentives-availability';

export const useRewardsEvaluation = () => {
  const { engineRewardsMap, sanityRewardsMap } = useLoyaltyRewardsList();
  const { prices, store } = useStoreContext();
  const { vendor } = usePosVendor();
  const { activeDayParts, dayParts } = useMainMenuContext();
  const [rewardsEvaluationMap, setRewardsEvaluationMap] = useState<IncentiveEvaluationMap>({});
  const [isRewardsEvaluationLoading, setIsRewardsEvaluationLoading] = useState(true);
  const { rewardsCooldown } = useLoyaltyContext();
  const { checkIncentiveAvailability } = useLoyaltyIncentivesAvailabilityUtils();

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, indexedRewards, isLoading: isUnifiedMenuLoading } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();

  useEffect(() => {
    const isWaitingForDependencies =
      isEmpty(sanityRewardsMap) ||
      isEmpty(engineRewardsMap) ||
      (!isUnifiedMenuEnabled && !prices) ||
      (isUnifiedMenuEnabled && (!indexedMenu || !posServiceMode)) ||
      (isUnifiedMenuEnabled && isUnifiedMenuLoading);

    if (isWaitingForDependencies) {
      setIsRewardsEvaluationLoading(true);
      return;
    }

    let isCancelled = false;
    setIsRewardsEvaluationLoading(true);

    const buildEvaluationResultsMap = async () => {
      const evaluationsMap = {} as IncentiveEvaluationMap;

      for (const sanityReward of Object.values(sanityRewardsMap ?? {})) {
        const loyaltyEngineId = sanityReward?.loyaltyEngineId ?? '';
        const engineReward = engineRewardsMap[loyaltyEngineId];

        // locked === not enough loyalty points
        // availability must still be evaluated even if the reward is locked
        if (!engineReward) {
          evaluationsMap[loyaltyEngineId] = [];
          continue;
        }

        const isRewardAvailable = isUnifiedMenuEnabled
          ? await checkIncentiveAvailability(sanityReward._id)
          : itemIsAvailable(sanityReward as IWithVendorConfig, vendor, prices);

        // If the reward is not available, we build a vendor evaluation result
        // and continue to the next reward.
        if (!isRewardAvailable) {
          evaluationsMap[loyaltyEngineId] = [buildVendorEvaluationResult()];
          continue;
        }

        const incentive = sanityReward?.incentives?.[0] ?? undefined;
        let status: ItemAvailabilityStatus | null = null;
        if (isUnifiedMenuEnabled) {
          const rewardEntry = indexedRewards.find(sanityReward._id);
          const entryId = rewardEntry?.options?.[0]?.entryId;
          // this check make sure that the reward benefit is available for the current daypart
          // if not, we build a vendor evaluation result and continue to the next reward.
          if (rewardEntry && entryId) {
            const menuEntry = indexedMenu.find(entryId);
            status = getOneMenuAvailability(
              menuEntry,
              activeDayParts,
              posServiceMode,
              Boolean(store.hasBurgersForBreakfast),
              isEntryAvailable
            );
          }
        } else {
          status = getAvailabilityStatus({
            isReward: true,
            data: incentive as IMenuObjectWithDaypart,
            activeDayParts,
            prices,
            vendor,
            isExtra: false,
            storeHasBurgersForBreakfast: Boolean(store.hasBurgersForBreakfast),
          });
        }

        let evaluationsResult: any = null;
        switch (status) {
          case ItemAvailabilityStatus.AVAILABLE:
          case ItemAvailabilityStatus.STORE_NOT_SELECTED:
            break;
          case ItemAvailabilityStatus.OUT_OF_DAYPART:
            evaluationsResult = buildOutOfDayPartEvaluationResult(sanityReward, dayParts);
            break;
          default:
            evaluationsResult = buildVendorEvaluationResult();
        }

        if (evaluationsResult) {
          evaluationsMap[loyaltyEngineId] = [evaluationsResult];
        } else if (rewardsCooldown.isActive) {
          evaluationsMap[loyaltyEngineId] = [buildInCooldownEvaluationResult()];
        }
      }

      return evaluationsMap;
    };

    // This`void` is intentionally,
    // is basically saying that we don't want to wait for the promise to resolve
    // inside the useEffect.
    void buildEvaluationResultsMap().then(evaluationsMap => {
      if (!isCancelled) {
        setRewardsEvaluationMap(evaluationsMap);
        setIsRewardsEvaluationLoading(false);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [
    activeDayParts,
    dayParts,
    engineRewardsMap,
    indexedMenu,
    isUnifiedMenuEnabled,
    isUnifiedMenuLoading,
    posServiceMode,
    prices,
    sanityRewardsMap,
    store.hasBurgersForBreakfast,
    vendor,
    rewardsCooldown.isActive,
    checkIncentiveAvailability,
    indexedRewards,
  ]);

  return {
    rewardsEvaluationMap,
    isRewardsEvaluationLoading,
    engineRewardsMap,
    sanityRewardsMap,
  };
};
